import {
  Component,
  OnInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import { PlatformInformationService } from '../../../../../projects/platform-manager/src/lib/services/platform-information.service';
import { TranslateService } from '@ngx-translate/core';
import { BookingParametersInterface } from '../../../shared/entities/bookingParameters';
import { ShopService } from '../../../shared/services/shop.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { ResolutionService } from '../../../core/services/resolution.service';
import { Meta, Title } from '@angular/platform-browser';
import { TagManagerService } from '../../../core/services/tag-manager.service';
import { SurrogateKeyService } from '../../../core/services/surrogate-key.service';
import { SkihirePoiInterface } from '../../../shared/interfaces/skihire-poi-interface';
import { ApiService } from '../../../core/services/api.service';
import { ScriptLoaderService } from '../../../core/services/scriptloader.service';
import { DiscountTickerService } from '../../../core/services/discount-ticker.service';
import { DiscounttickerComponent } from '../../../shared/saleslocation/shop/discountticker/discountticker.component';
import { DeferLoadModule } from '@trademe/ng-defer-load';
import { OffersComponent } from '../offers/offers.component';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// defer loaded component starting with Ang17
import { TopTownsComponent } from '../top-towns/top-towns.component';
import { TopCountriesComponent } from '../top-countries/top-countries.component';
import { BookWithUsComponent } from '../book-with-us/book-with-us/book-with-us.component';
import { Simply2skiSEOComponent } from '../simply2ski-seo/simply2ski-seo.component';
import { ShopListAdvantagesComponent } from '../../saleslocation/shop-list-advantages/shop-list-advantages.component';
import { HeroSliderComponent } from '../hero-slider/hero-slider.component';
import { DiscountCountdownInterface } from '../../../shared/interfaces/discount-countdown-interface';
import { SpecialSalesService } from '../../../core/services/special-sales.service';
import { startWith } from 'rxjs';
import { LcpPreloaderService } from '../../../core/services/lcp-preloader.service';
import { PlatformassetPipe } from '../../../../../projects/platform-manager/src/public_api';
import { ShopSearchComponent } from '../../../shared/landing-page/search/shop-search/shop-search.component';
import { HeroSliderNavComponent } from '@shared/components';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import { TestimonialsComponent } from '../../../shared/testimonials/testimonials/testimonials.component';
import { CrowdinTranslationService } from '../../../shared/services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';
import { LocalizeRouterPipe } from '@gilsdav/ngx-translate-router';
import { StructuredDataService } from '../../../shared/services/structured-data.service';

@Component({
  selector: 'app-start',
  templateUrl: './start.component.html',
  styleUrls: ['./start.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DeferLoadModule,
    AlpitranslatePipe,
    OffersComponent,
    HeroSliderNavComponent,
    // RouterModule,
    RouterLink,
    LocalizeRouterPipe,
    PlatformassetPipe,
    // defer loaded with Ang17
    TopTownsComponent,
    TopCountriesComponent,
    BookWithUsComponent,
    Simply2skiSEOComponent,
    ShopListAdvantagesComponent,
    HeroSliderComponent,
    DiscounttickerComponent,
    ShopSearchComponent,
    TestimonialsComponent
  ]
})
export class StartComponent implements OnInit, AfterViewInit {
  isMobileDevice = this.resolutionService.isMobileDevice();
  isLargeDevice = this.resolutionService.isLargeDevice();
  isSmallerThanLargeDevice = this.resolutionService.isSmallerThanLargeDevice();
  isServerDevice = this.resolutionService.isServer();
  platformShortCode = this.platformService.getCurrentPlatformShortcode();
  displayDesktopElements = false;
  deliveryShop?: { id: string; price: number };

  upToDiscount =
    this.specialSaleService.getActiveSale()?.upToDiscount ??
    (this.platformService.getCurrentPlatformShortcode() === 'pf' ? 50 : 60);

  protected discount: DiscountCountdownInterface;

  constructor(
    public platformService: PlatformInformationService,
    public translateService: TranslateService,
    protected shopService: ShopService,
    protected navigationService: NavigationService,
    private resolutionService: ResolutionService,
    protected title: Title,
    protected meta: Meta,
    protected tagManagerService: TagManagerService,
    protected surrogateKeyService: SurrogateKeyService,
    private apiService: ApiService,
    protected scriptLoaderService: ScriptLoaderService,
    protected discountTickerService: DiscountTickerService,
    private specialSaleService: SpecialSalesService,
    private lcpLoaderService: LcpPreloaderService,
    private assetPipe: PlatformassetPipe,
    private crowdinService: CrowdinTranslationService,
    private structuredDataService: StructuredDataService,
    private route: ActivatedRoute,
    protected cdr: ChangeDetectorRef,
    protected destroyRef: DestroyRef
  ) {
    // adds 'start' as a surrogate key to the default of 'all alpinresorts-angular test|beta|prod'
    surrogateKeyService.addSurrogateKeys(['start']);
  }

  protected clearDiscountTicker() {
    this.discount = undefined;
    this.cdr.detectChanges();
  }

  private onLanguageChange() {
    this.structuredDataService.setAlternateLangLinks(this.route.snapshot.pathFromRoot);
    const prefix = `START.${this.platformShortCode.toUpperCase()}`;

    this.structuredDataService.setMetaTags([
      {
        name: 'og:title',
        content: `${prefix}.openGraphTitle`
      },
      {
        name: 'description',
        content: `${prefix}.metaDescription`
      },
      { name: 'og:description', content: `${prefix}.openGraphDescription` }
    ]);
    // update the page title
    this.translateService
      .get(`${prefix}.title`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: string) => {
        this.title.setTitle(res);
      });
    // add LCP preload 🚀 to virtual content since mobile and dektop
    // share the same hero image but in different components 🙃
    if (!this.platformService.getIsBrowser() && this.platformService.getCurrentPlatformShortcode() === 'ar') {
      this.lcpLoaderService.addPreloadLink(this.assetPipe.transform('images/heros/asterix.webp', true));
    }
  }

  ngOnInit() {
    this.crowdinService.watchLangChange({
      loaderMap: loaderMap,
      destroyRef: this.destroyRef,
      successCallback: () => {
        this.onLanguageChange();
      }
    });

    if (!this.platformService.getIsBrowser()) {
      this.displayDesktopElements = false;
    } else {
      this.displayDesktopElements =
        this.resolutionService.getCurrentSizeReference() > this.resolutionService.mediumReference;
      this.resolutionService.sizeReferenceChange
        .pipe(
          startWith(this.resolutionService.getCurrentSizeReference()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(currentSizeReference => {
          this.isMobileDevice = this.resolutionService.isMobileDevice();
          this.isLargeDevice = this.resolutionService.isLargeDevice();
          this.isSmallerThanLargeDevice = this.resolutionService.isSmallerThanLargeDevice();
          this.displayDesktopElements = currentSizeReference > this.resolutionService.mediumReference;
          this.cdr.markForCheck();
          this.cdr.detectChanges();
          if (this.isSmallerThanLargeDevice) {
            this.initTickerOnDemand();
          }
        });
      if (this.resolutionService.isSmallerThanLargeDevice()) {
        this.initTickerOnDemand();
      }
    }
  }

  private initTickerOnDemand() {
    this.discountTickerService.discounts$.subscribe(discounts => {
      this.discount = discounts.find(d => +d.timestamp > Date.now() / 1000);
    });
  }

  /**
   * Action, when form is valid and the submit-button is clicked
   * @param event
   */
  updateForm(event: BookingParametersInterface) {
    this.shopService.updateBookingParameters(event);
    // this.shopService.cacheBookingParameters(event); // TODO: writing the bps should be enough
    if (event.poi) {
      if (event.poi.type === 'AlpinShop') {
        this.navigationService.navigateToProductSelection(event.poi, event);
      } else {
        this.navigationService.navigateToPoi(event.poi, true);
      }
    } else {
      this.navigationService.navigateToSearchPage(event.slug);
    }
  }

  skipToDeliveryShop(event: { params: BookingParametersInterface; shopId: string }) {
    this.shopService.updateBookingParameters(event.params);
    this.navigationService.navigateToProductSelection(
      { foreign_id: event.shopId } as unknown as SkihirePoiInterface,
      event.params
    );
  }

  /**
   * will preload towninfo before searchform will be sent to improve percieved speed
   * @param event shopsearch event
   */
  prefetchLocation(event: { slug: string; poi: SkihirePoiInterface }) {
    this.apiService.getLocationsBySlug({ slug: event.slug }).subscribe({
      next: res => {
        const shopId =
          res.find(location => location.vsLocationDropOff)?.id ||
          res.find(location => location.vsEquipmentToLocation)?.id;
        if (shopId && (event.poi.country_code?.toLowerCase() === 'at' || !event.poi.country_code)) {
          this.deliveryShop = {
            id: shopId,
            price: res.find(location => location.id === shopId).defaultProductOnlinePriceRaw
          };
        } else {
          this.deliveryShop = undefined;
        }

        this.cdr.detectChanges();
      },
      error: err => {
        console.error('start', err);
      }
    });
    this.apiService.getTownBySlug(event.slug).subscribe({
      error: err => {
        console.error('start', err);
      }
    });
  }

  ngAfterViewInit() {
    this.tagManagerService.push({ cpt: 'Homepage', cem: '' });
  }
}
